// Migration — the hand-entry checklist for the legacy → v2 handover.
//
// Operator ruling 2026-08-21: the mirror is stopped, and every legacy event
// from the handover instant onward is re-entered BY HAND in this console, with
// the books checked after each one.
//
// WHAT THIS SCREEN IS FOR
// One row per legacy event still to enter, carrying everything needed to
// replay it — party, direction, quantity, rate, the operator's own note — so
// the legacy admin never has to be opened alongside. Mark it once booked, and
// the row records WHICH v2 deal settled it. That link is the whole point: a
// hand-booked trade gets a fresh DEAL-… id with no path back to the legacy row,
// so without it there is no way to prove the two systems agree event by event.
//
// WHAT THIS SCREEN IS NOT
// It does not compute the comparison. scripts/migration-reconcile.mjs owns
// that — the BFF is a Worker and cannot reach legacy Mongo, so a comparison
// computed here would be books-against-books and would look authoritative while
// meaning nothing. What is shown under a row is the verdict that script
// recorded after that entry.
//
// Legacy timestamps are DATA-ENTRY time, not event time (see
// docs/HOW-THE-DESK-ACTUALLY-WORKS.md), so the order here is the order the
// operator typed them — usually but NOT always the order they happened.
//
// THE PHONE PASS (19 Sept 2026, the sweep): the header is the one
// ScreenHeader (the title owns its line on a phone; Refresh under it; the
// closed-history paragraphs in "About this screen"); the status tabs are a
// chip strip that scrolls sideways below lg; each row is the ledger card's
// two lines — the kind pill and the words, then the stamp and the party —
// with the status pill under them on a phone and at the right at lg+; the
// Mark panel's boxes are Fields on the .input class, its buttons a grid
// with Entered full width at the bottom below lg. The operator's note, a
// skip reason and the reconcile verdict are content, so they sit at the
// 13px content floor, not the 12px hint size. Nothing the screen reads
// or writes has changed.

import { useState } from 'react';
import { Pending } from '../components/Pending';
import { EmptyState } from '../components/kit';
import { LAST_READ_NONE, ReadFailed, Refused } from '../components/OnboardingParts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, AlertTriangle, SkipForward, Ban, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import { partyName, shortReference } from '../lib/naming';
import type { MigrationResponse, MigrationRow, MigrationStatus } from '../lib/api';
import { displayAsset, fmtAmount, fmtDate, fmtQuantity, fmtRate, fmtRelative } from '../lib/format';
import { ScreenHeader } from '../components/ScreenHeader';
import { Field } from '../components/Field';
import { Pill } from '../components/Pill';
import { Amount, KindPill, LEDGER } from '../components/Ledger';
import { CopyRef } from '../components/CopyRef';

const TABS: Array<{ key: MigrationStatus | 'all'; label: string }> = [
  { key: 'pending', label: 'To enter' },
  { key: 'entered', label: 'Done' },
  { key: 'skipped', label: 'Skipped' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'all',     label: 'All' },
];

function Bar({ p }: { p: MigrationResponse['progress'] }) {
  const done = p.entered + p.skipped;
  const pct = p.total ? Math.round((done / p.total) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap justify-between gap-x-3 text-meta text-tg-hint">
        <span>{p.entered} entered · {p.skipped} skipped · {p.blocked} blocked</span>
        <span className="font-mono tabular">{p.pending} to go</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-panel-2">
        <div className="h-full rounded-full bg-pos transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/** The word on the row's kind pill: the verb the sentence used to open
 *  with — Sell / Buy for a trade, the legacy entry's own type otherwise.
 *  The raw kind ("txn" / "pri") never reaches the screen. */
function kindWord(r: MigrationRow): string {
  if (r.kind === 'txn') return r.tran_from?.startsWith('sale') ? 'Sell' : 'Buy';
  const t = r.tran_type ?? '';
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
}

/** A booked deal's leg as the BFF sends it — "33640512.5 USDT", the figure and its asset joined as text — in the books' own
 *  format: "33,640,512.5 USDT". Anything not of that shape is shown as it came. */
export function groupedFigure(text: string): string {
  const m = /^(-?\d+(?:\.\d+)?)\s+(\S+)$/.exec(text.trim());
  return m ? `${fmtQuantity(m[1], m[2])} ${displayAsset(m[2])}` : text;
}

/** What the operator has to reproduce in the console, in words — the
 *  figures after the kind pill, each amount one unbreakable piece. */
function Instruction({ r }: { r: MigrationRow }) {
  if (r.kind === 'txn') {
    return (
      <span>
        <Amount value={r.quantity ?? ''} asset={r.asset ?? ''} />{' '}
        <span className="amt text-tg-hint whitespace-nowrap">@ {fmtRate(r.rate, r.quote_ccy ?? undefined)}</span>{' '}
        <span className="text-tg-hint">=</span>{' '}
        <Amount value={r.total ?? ''} asset={r.quote_ccy ?? ''} />
      </span>
    );
  }
  return (
    <span>
      <Amount value={r.quantity ?? ''} asset={r.asset ?? ''} />
      {r.party && r.counterparty && (
        <span className="text-tg-hint">
          {' '}· {partyName({ display_name: r.party })} <ArrowRight size={11} className="inline" /> {partyName({ display_name: r.counterparty })}
        </span>
      )}
    </span>
  );
}

/** The row's state, in a word: under the words on a phone, at the right at
 *  lg+. An entered row carries its deal's short reference, copyable. */
function StatusMark({ r }: { r: MigrationRow }) {
  if (r.status === 'entered') {
    return (
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 shrink-0 lg:justify-end">
        <Pill tone="good" icon={<CheckCircle2 size={11} />}>Entered</Pill>
        {r.entered_deal_id && <CopyRef value={r.entered_deal_id} label={shortReference(r.entered_deal_id)} />}
      </span>
    );
  }
  if (r.status === 'skipped') return <span className="shrink-0"><Pill icon={<SkipForward size={11} />}>Skipped</Pill></span>;
  if (r.status === 'blocked') return <span className="shrink-0"><Pill tone="warn" icon={<Ban size={11} />}>Blocked</Pill></span>;
  return null;
}

function DriftNote({ r }: { r: MigrationRow }) {
  const d = r.drift_after;
  if (!d) return null;
  if (!d.drift?.length) {
    return (
      <p className="flex items-center gap-1.5 text-body text-pos">
        <CheckCircle2 size={13} className="shrink-0" /> Checked after this entry — {d.checked} {d.checked === 1 ? 'balance agrees' : 'balances agree'} with legacy.
      </p>
    );
  }
  return (
    <div className="rounded-lg bg-warn-bg p-2">
      <p className="flex items-center gap-1.5 text-body font-medium text-warn">
        <AlertTriangle size={13} className="shrink-0" /> {d.drift.length} {d.drift.length === 1 ? 'balance disagrees' : 'balances disagree'} after this entry
      </p>
      <ul className="mt-1 space-y-0.5">
        {d.drift.slice(0, 4).map((x) => (
          <li key={`${x.slug}-${x.asset}`} className="text-body text-warn">
            {x.name} {displayAsset(x.asset)} — legacy{' '}
            <span className="amt">{fmtAmount(x.legacy, x.asset)}</span>, books{' '}
            <span className="amt">{fmtAmount(x.actual, x.asset)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Carry a legacy row's own facts into the screen that books it.
 *
 * The checklist used to say "book it elsewhere, then type the id back here",
 * and the operator's first attempt at that was the literal words DEAL BOOKED.
 * Facts travel; judgment does not. Quantity, total, party, note and the legacy
 * reference are copied. The RAILS are not — which pot the money moved through
 * is the operator's call, and a guessed rail looks authoritative while being
 * silently wrong. Direction follows RULING C from the row's own party and
 * tran_type, and stays visible so it can be corrected.
 */

export function Migration() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<MigrationStatus | 'all'>('pending');
  const [open, setOpen] = useState<string | null>(null);
  const [dealId, setDealId] = useState('');
  const [reason, setReason] = useState('');

  const q = useQuery<MigrationResponse>({
    queryKey: ['migration', tab],
    queryFn: () => api.migrationList(tab),
    // This list changes only because the operator just changed it, so a cached
    // copy is never what they want to see. The BFF reads through Hyperdrive's
    // cache for the same reason.
    staleTime: 0,
  });

  const mark = useMutation({
    mutationFn: (v: { legacyId: string; status: MigrationStatus; deal_id?: string; reason?: string }) =>
      api.migrationMark(v.legacyId, { status: v.status, deal_id: v.deal_id, reason: v.reason }),
    onSuccess: () => {
      setOpen(null); setDealId(''); setReason('');
      qc.invalidateQueries({ queryKey: ['migration'] });
    },
  });

  const rows = q.data?.rows ?? [];

  return (
    <div className="space-y-4">
      <ScreenHeader
        title="Legacy history"
        subtitle="Closed history — nothing here is booked into the live books."
        actions={
          <button
            type="button"
            onClick={() => qc.invalidateQueries({ queryKey: ['migration'] })}
            disabled={q.isFetching}
            className="btn-secondary text-body py-1.5 shrink-0 disabled:opacity-50"
          >
            {q.isFetching ? 'Reading…' : 'Refresh'}
          </button>
        }
        help={
          <>
            <p>
              The legacy books ended at the 1 September opening count and everything below is
              already inside the opening balances. Nothing here is booked into the live books —
              this page is kept so the old entries stay findable.
            </p>
            <p>
              The list is a snapshot, as fresh as its read time. Marking a row records which deal
              it was booked as — the only link back to the legacy entry — or why it was skipped
              or blocked. After each entry, run the reconcile check by hand — it compares the books
              with legacy and records its verdict under that row.
            </p>
          </>
        }
      >
        {/* WHAT THIS LIST IS. Not live against legacy — a snapshot that
            scripts/migration-worklist.mjs pulls. Saying so, with the age, is
            the difference between "nothing to enter" and "nobody has looked":
            on 2026-08-24 the page showed an empty checklist while two trades
            were waiting, and it was right about the snapshot. */}
        <p className="text-meta text-tg-hint">
          {q.data?.worklist_refreshed_at ? (
            <>Legacy last read <span className="font-mono tabular">
              {fmtRelative(q.data.worklist_refreshed_at)}</span></>
          ) : 'Legacy read time unknown'}
        </p>
      </ScreenHeader>

      {q.data?.handover && (
        <section className="card">
          <p className="text-meta text-tg-hint">
            Handover taken{' '}
            <span className="font-mono tabular">
              {fmtDate(q.data.handover.taken_at)}
            </span>
            {q.data.handover.mirror_cursor && (
              <> · mirror stopped at{' '}
                <span className="font-mono tabular">
                  {fmtDate(q.data.handover.mirror_cursor)}
                </span>
              </>
            )}
          </p>
          {q.data.progress.total > 0 && <div className="mt-2"><Bar p={q.data.progress} /></div>}
        </section>
      )}

      {/* ON THE PHONE the five tabs are one strip that scrolls sideways (the
          PeriodChips shape) rather than two ragged lines; at lg+ they wrap. */}
      <div className="flex flex-nowrap gap-1.5 max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:flex-wrap lg:gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => { setTab(t.key); setOpen(null); }}
            className={`chip ${tab === t.key ? 'chip-on' : ''}`}
          >
            {t.label}
            {t.key !== 'all' && q.data?.progress
              ? <span className="ml-1.5 text-tg-hint">{q.data.progress[t.key as keyof MigrationResponse['progress']]}</span>
              : null}
          </button>
        ))}
      </div>

      {/* PENDING, not "loading": a first read opened with no connection is paused (no answer, nothing fetching) and waits. */}
      {q.isPending && <Pending />}
      {/* AN EMPTY CLAIM NEEDS A SUCCESSFUL READ (22 Oct 2026): not a failed one — it drew "Nothing here." beside its own red sentence —
          and not one that has not come. */}
      {q.isSuccess && !rows.length && <EmptyState>Nothing here.</EmptyState>}
      {q.isError && (
        <ReadFailed what="legacy history" error={q.error} says={rows.length > 0 ? 'showing the last good read' : q.data ? LAST_READ_NONE : undefined} retry={() => { void q.refetch(); }} />
      )}

      <div className="space-y-2">
        {rows.map((r) => {
          const isOpen = open === r.legacy_id;
          const word = kindWord(r);
          return (
            <section key={r.legacy_id} className="card space-y-2">
              {/* THE LEDGER CARD'S TWO LINES: the kind pill and the words,
                  then the stamp and the party; the status under them on a
                  phone, at the right at lg+. */}
              <div className="flex flex-col gap-1.5 lg:flex-row lg:items-start lg:justify-between lg:gap-3">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className={`${LEDGER.cardLine1} lg:text-body`}>
                    {word && <KindPill label={word} className="shrink-0" />}
                    <span className="min-w-0"><Instruction r={r} /></span>
                  </div>
                  <p className="flex flex-wrap items-baseline gap-x-2 text-meta text-tg-hint">
                    <span className="font-mono tabular whitespace-nowrap">{fmtDate(r.occurred_at)}</span>
                    {r.party && <span className="who min-w-0 truncate">{partyName({ display_name: r.party })}</span>}
                  </p>
                  {r.description && (
                    <p className="text-body text-tg-hint">note: &ldquo;{r.description}&rdquo;</p>
                  )}
                </div>
                <StatusMark r={r} />
              </div>

              {r.skip_reason && <p className="text-body text-tg-hint">reason: {r.skip_reason}</p>}
              <DriftNote r={r} />

              {r.status === 'pending' && !isOpen && (
                <button type="button" className="btn-secondary px-3 py-1.5 text-body" onClick={() => setOpen(r.legacy_id)}>
                  Mark…
                </button>
              )}

              {isOpen && (
                <div className="space-y-3 rounded-lg bg-panel-2 p-2.5">
                  <Field label="Deal id it was booked as" hint="optional">
                    <input
                      className="input font-mono"
                      placeholder="DEAL-2026-08-21-XXXXXX"
                      value={dealId}
                      onChange={(e) => setDealId(e.target.value)}
                    />
                  </Field>
                  {/* Click, do not type. The reference guard refuses anything
                      that is not a real minted id — correctly, after three
                      movements were lost to the word "Posted" — but leaving the
                      operator to go and find one produced "DEAL BOOKED" as the
                      very next attempt. These are deals whose amounts match this
                      row on either leg. */}
                  {/* NO BOOKING FROM HERE. The legacy era closed at the 1 Sept
                      opening count, and these rows are already inside the
                      opening balances. A prefilled "Book this trade" link was a
                      live path to entering a post-cutover legacy row — or the
                      same trade twice — into the fresh books (3 Sept audit). */}
                  {(r.candidates?.length ?? 0) === 0 && (
                    <p className="text-meta text-tg-hint">No booked deal matches this row.</p>
                  )}
                  {(r.candidates?.length ?? 0) > 0 && (
                    <div className="space-y-1">
                      <p className="text-meta text-tg-hint">
                        Booked deals matching this row — click to use:
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {r.candidates!.map((cd) => (
                          <button
                            key={cd.deal_id}
                            type="button"
                            onClick={() => setDealId(cd.deal_id)}
                            title={`${cd.deal_id} · ${cd.direction} · take ${cd.take} · give ${cd.give}`}
                            className={`max-w-full rounded border px-2 py-1 font-mono text-meta transition-colors ${
                              dealId === cd.deal_id
                                ? 'border-tg-accent bg-tg-accent/[0.14] text-tg-accent'
                                : 'border-stroke bg-panel text-tg-text hover:border-brand-muted'
                            }`}
                          >
                            {shortReference(cd.deal_id)}
                            <span className="ml-1.5 text-tg-hint">{groupedFigure(cd.give)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <Field label="Reason" hint="required to skip or block">
                    <input
                      className="input"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </Field>
                  {/* Three buttons across the phone and Entered full width
                      beneath them; one row at lg+, Entered first. */}
                  <div className="grid grid-cols-3 gap-2 lg:flex lg:flex-wrap">
                    <button
                      type="button"
                      className="btn-primary px-3 py-1.5 text-body col-span-3 order-last w-full lg:order-none lg:w-auto"
                      disabled={mark.isPending}
                      onClick={() => mark.mutate({ legacyId: r.legacy_id, status: 'entered', deal_id: dealId.trim() || undefined })}
                    >
                      Entered
                    </button>
                    <button
                      type="button"
                      className="btn-secondary px-3 py-1.5 text-body"
                      disabled={mark.isPending || !reason.trim()}
                      onClick={() => mark.mutate({ legacyId: r.legacy_id, status: 'skipped', reason: reason.trim() })}
                    >
                      Skip
                    </button>
                    <button
                      type="button"
                      className="btn-secondary px-3 py-1.5 text-body"
                      disabled={mark.isPending || !reason.trim()}
                      onClick={() => mark.mutate({ legacyId: r.legacy_id, status: 'blocked', reason: reason.trim() })}
                    >
                      Block
                    </button>
                    <button type="button" className="btn-secondary px-3 py-1.5 text-body" onClick={() => setOpen(null)}>
                      Cancel
                    </button>
                  </div>
                  {mark.isError && <Refused error={mark.error} />}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
